import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import './CarePlusPlanCard.css';
import * as translator from '../../../../../utils/translator';
import * as http from '../../../../../utils/http';
import { PLAN_TYPE_LABEL } from '../../CarePlusOrderDetail/carePlusConstants';
import { fillWeight, formatWeight, useWeightUnit } from '../../../../../utils/weightUnits';
import heroImage from '../../../../../images/CarePlus/mounjaro-hero-v2.png';
import weightLostIcon from '../../../../../images/CarePlus/weight-lost-icon.png';

interface CarePlusPlanCardProps {
  subscriptionHashID: string;
}

const CarePlusPlanCard: React.FC<CarePlusPlanCardProps> = ({ subscriptionHashID }) => {
  const { country, language } = useOutletContext<any>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<CarePlusSubscriptionDetail | null>(null);

  // planType is not on the asidebar payload, so the card fetches the subscription detail itself
  useEffect(() => {
    http.getFromServer(`${country}/account/care-plus-subscription/${subscriptionHashID}/details`, [
      http.MIDDLEWARES.CUSTOMER_ID,
      http.MIDDLEWARES.CART_ORDER,
    ])
      .then((res: any) => setDetail(res.data))
      .catch((err) => window.clientLogger.error('Error loading Care+ plan card: ', err));
  }, [subscriptionHashID]);

  const t = (path: string) => translator.translate(language, 'my_account', path);
  const locale = translator.getLocaleByCountry(country);
  const [weightUnit] = useWeightUnit(country);

  const statusBadgeKey: Record<string, string> = {
    active: 'CarePlus/landing/heroBadge',
    paused: 'CarePlus/landing/heroBadgePaused',
    cancelled: 'CarePlus/landing/heroBadgeCancelled',
    pending: 'CarePlus/landing/heroBadgePending',
  };
  const heroBadge = t(statusBadgeKey[detail?.subscription.status ?? 'active'] ?? statusBadgeKey.active);
  const planIncludes = t('CarePlus/landing/planIncludes');

  const planType = detail?.subscription.planType;
  const isMonthlyPlan = Boolean(detail?.subscription.isMonthlyPlan);
  const planTypeLabel = isMonthlyPlan ? t('CarePlus/monthlyPlan/label') : planType ? PLAN_TYPE_LABEL[planType] : '';
  const subscription = detail?.subscription;

  // UK only: compact weight state + next renewal (other countries keep the original card).
  const isUk = country?.toLowerCase() === 'gb';

  // Served by the same weight builder as the Care+ dashboard (/care-profile), so both
  // surfaces stay in sync as new entries are logged. 0 is only a display fallback for
  // patients with no weight data at all (UK: missing values are never shown as 0, see below).
  const currentWeight = subscription?.currentWeight ?? 0;
  const startWeight = subscription?.startWeight ?? 0;
  const goalWeight = subscription?.goalWeight ?? 0;
  const totalLost = startWeight - currentWeight;
  const toGoal = currentWeight - goalWeight;
  const progressPercent = startWeight !== goalWeight
    ? Math.min(100, Math.max(0, ((startWeight - currentWeight) / (startWeight - goalWeight)) * 100))
    : 0;
  // UK compact state: no zero-value progress. The tracker needs a current weight; the lost / to-goal /
  // progress parts also need start + goal and some weight actually lost.
  const hasCurrentWeight = !isUk || subscription?.currentWeight != null;
  const hasWeightProgress = !isUk || (hasCurrentWeight && subscription?.startWeight != null && subscription?.goalWeight != null && totalLost > 0);

  const medicationName = detail?.subscription.medicationName ?? '';
  const [planNamePrefix, planNameSuffix] = (t('CarePlus/landing/planName') || '').replace(':medicationName', medicationName).split(':tier');

  return (
    <div className="careplus-plan-card w-full">
      <div className="careplus-plan-card__top-row">
        <div className="careplus-plan-card__photo-block">
          <div className="careplus-plan-card__hero">
            <img className="careplus-plan-card__hero-image" src={heroImage} alt="Care+" />
          </div>

          {hasCurrentWeight && (
          <div className="careplus-plan-card__weight-tracker">
            <div className="careplus-plan-card__weight-header">
              <div className="careplus-plan-card__weight-current">
                <span className="careplus-plan-card__weight-current-label">{t('CarePlus/landing/currentWeight')}</span>
                <span className="careplus-plan-card__weight-current-value">
                  {weightUnit === 'kg' ? <>{currentWeight}<span>kg</span></> : formatWeight(currentWeight, weightUnit)}
                </span>
              </div>
              {hasWeightProgress && (
              <div className="careplus-plan-card__weight-lost">
                <span className="careplus-plan-card__weight-lost-pill">
                  <img src={weightLostIcon} alt="" />
                  {fillWeight(t('CarePlus/landing/totalLost') || '', ':kg', totalLost, weightUnit)}
                </span>
                <span className="careplus-plan-card__weight-to-goal">
                  {fillWeight(t('CarePlus/landing/toGoal') || '', ':kg', Math.max(0, toGoal), weightUnit)}
                </span>
              </div>
              )}
            </div>
            {hasWeightProgress && (
            <>
            <div className="careplus-plan-card__weight-bar-track">
              <div className="careplus-plan-card__weight-bar-fill" style={{ width: `${progressPercent}%` }} />
            </div>
            <div className="careplus-plan-card__weight-milestones">
              <span>
                <span className="careplus-plan-card__milestone-label">{t('CarePlus/landing/start')}</span>
                <span className="careplus-plan-card__milestone-value">{formatWeight(startWeight, weightUnit)}</span>
              </span>
              <span>
                <span className="careplus-plan-card__milestone-label">{t('CarePlus/landing/progress')}</span>
                <span className="careplus-plan-card__milestone-progress">{Math.round(progressPercent)}%</span>
              </span>
              <span>
                <span className="careplus-plan-card__milestone-label">{t('CarePlus/landing/goal')}</span>
                <span className="careplus-plan-card__milestone-value">{formatWeight(goalWeight, weightUnit)}</span>
              </span>
            </div>
            </>
            )}
          </div>
          )}
        </div>

        {planType && (
          <div className="careplus-plan-card__status-col">
            <span className="careplus-plan-card__plan-type-badge">{heroBadge}</span>
            <span className="careplus-plan-card__plan-type-name">
              <span className="careplus-plan-card__plan-type-name-brand">{planNamePrefix}</span>
              <span className="careplus-plan-card__plan-type-name-type">{planTypeLabel}{isMonthlyPlan ? '' : planNameSuffix}</span>
            </span>
            {!isUk && (
              <div className="careplus-plan-card__quick-info">
                <div className="careplus-plan-card__quick-info-item">
                  <span className="careplus-plan-card__quick-info-label">{t('CarePlus/detail/termEnd')}</span>
                  <span className="careplus-plan-card__quick-info-value">–</span>
                </div>
                <div className="careplus-plan-card__quick-info-item">
                  <span className="careplus-plan-card__quick-info-label">{t('CarePlus/detail/videoConsultations')}</span>
                  <span className="careplus-plan-card__quick-info-value">0</span>
                </div>
              </div>
            )}
            {isUk && subscription?.status === 'active' && subscription.nextRenewalDate && (
              <div className="careplus-plan-card__quick-info">
                <div className="careplus-plan-card__quick-info-item">
                  <span className="careplus-plan-card__quick-info-label">{t('CarePlus/detail/nextRenewal')}</span>
                  <span className="careplus-plan-card__quick-info-value">
                    {new Date(subscription.nextRenewalDate).toLocaleDateString(locale)}
                    {subscription.nextRenewalAmount != null && ` · ${translator.getPrice(subscription.nextRenewalAmount, locale)}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="careplus-plan-card__includes">
        <p className="careplus-plan-card__includes-title">{planIncludes}</p>
        <ul className="careplus-plan-card__includes-list">
          <li>{t('CarePlus/upsell/benefit1')}</li>
          <li>{t('CarePlus/upsell/benefit2')}</li>
          <li>{t('CarePlus/upsell/benefit3')}</li>
          <li>{t('CarePlus/upsell/benefit4')}</li>
          <li>{t('CarePlus/upsell/benefit5')}</li>
        </ul>
      </div>

      <button
        type="button"
        className="careplus-plan-card__cta"
        onClick={() => navigate(`/${country}/account/care-profile`)}
      >
        {t('CarePlus/landing/openProgress')}
      </button>
    </div>
  );
};

export default CarePlusPlanCard;
